import React, { memo, useCallback, useMemo } from 'react';
import { Button } from '@core/components/Button';
import { Badge, Container } from 'reactstrap';
import { EmptySection } from '@core/components/EmptySection';
import { PageHeader } from '@modules/common';
import { openSelectEstimateModal } from './SelectEstimateModal';
import { useBloc } from '@core/utils/bloc';
import { CompactTMEstimateResponse } from '@business/entities/tm';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { MoreMenu } from '@modules/common/MoreMenu';
import { useCompany } from '@core/utils/hooks/misc';
import { Formatter } from '@core/utils/formatter';
import { toMoment } from '@core/utils/time';
import { CBody } from '@modules/common/CBody';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { EstimatesPageBloc } from './EstimatesPageBloc';
import { CommonService } from '@business/services';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useHistory } from '@core/router';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { EstimatorTemplateType } from '@business/entities/tm/TmTemplateEstimatorResponse';
import { OrgId } from '@business/entities/organization';

export interface EstimatesPageProps {
  viewType?: 'tabular' | 'page';
  orgId?: OrgId;
}

export const EstimatesPage = memo((props: EstimatesPageProps) => {
  const { viewType = 'page', orgId } = props;
  const bloc = useBloc(EstimatesPageBloc, orgId);
  const loading = useLoadingState(bloc.loading);
  const company = useCompany();
  const history = useHistory();

  const allEstimates = useNonNilObservable(bloc.estimates$);
  const estimates = useMemo(() => allEstimates.filter(e => e.type === EstimatorTemplateType.main), [allEstimates]);

  const columns: GCColumn<CompactTMEstimateResponse>[] = useMemo(
    () => [
      {
        name: 'Title',
        sortField: 'title',
        minWidth: '600px',
        cell: row => {
          return (
            <div className="d-flex flex-row mt-2 mb-2 align-items-center" onClick={() => {}}>
              <div style={{ position: 'relative' }}>
                <img
                  style={{
                    width: 80,
                    height: 105,
                    objectFit: 'cover',
                    objectPosition: 'top',
                    borderRadius: 10,
                  }}
                  src={'https://cdn.grandcentr.al/proposal-image.png'}
                />
                <Badge
                  className="d-flex flex-row p-1 align-items-center text-center"
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    right: 0,
                    fontSize: 12,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                    justifyContent: 'center',
                  }}
                  color={row.deletedAt ? 'danger' : 'info'}
                >
                  {row.deletedAt ? 'Archived' : 'Active'}
                </Badge>
              </div>
              <div className="d-flex flex-column ml-4">
                <a
                  className={`text-dark font-weight-medium font-size-16`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => history.push(`/estimates/${row.id}`)}
                >
                  {row.title}
                </a>
                <span className="text-muted font-size-12">Created By: {CommonService.getFullName(row.createdBy)}</span>
                <span className="text-muted font-size-12">Estimator: {row.estimatorUser ? CommonService.getFullName(row.estimatorUser) : 'N/A'}</span>
                <span className="text-muted font-size-12">
                  <div className="d-flex flex-row">
                    <span className="font-size-12">{gcLabel('Organizations')}: </span> <span className="font-size-12">{row.organization.name}</span>
                  </div>
                </span>
                <span className="text-muted font-size-12">
                  Budget: {Formatter.formatCurrency(row.budget, company.currencyCode)} on {toMoment(row.createdAt).format('MMM, DD YYYY')}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Status',
        sortField: 'status',
        width: '400px',
        cell: row => (
          <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', width: 300 }}>
            <div className="d-flex flex-column" style={{ justifyContent: 'flex-end', alignItems: 'center', width: 300 }}>
              <div className="d-flex flex-row">
                <a
                  onClick={() => {
                    // openEstimateDetail({ id: row.id, title: row.title })
                    history.push(`/estimates/${row.id}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Show Estimate
                </a>
              </div>
              <div className="d-flex flex-row" style={{ justifyContent: 'center', alignItems: 'flex-start', width: 300, marginTop: 5 }}>
                <div style={{ flex: 1, fontWeight: 400, fontSize: 12, width: 220 }} className="text-muted" />
                <div style={{ flex: 1, width: 80 }}>
                  <span className={`text-dark font-weight-medium font-size-16`}>{Formatter.formatCurrency(row.budget, company.currencyCode)}</span>
                </div>
              </div>
            </div>
            <div style={{ alignItems: 'center', flexDirection: 'row', display: 'flex' }}>
              <div className="d-flex">
                <MoreMenu
                  className="float-right"
                  vertical
                  items={[
                    {
                      icon: row.deletedAt ? 'fas fa-undo' : 'fas fa-archive',
                      className: row.deletedAt ? '' : 'text-danger',
                      title: row.deletedAt ? 'Restore' : 'Archive',
                      onClick: () => (row.deletedAt ? bloc.restoreEstimate(row.id).subscribe() : bloc.deleteEstimate(row.id).subscribe()),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        ),
      },
    ],
    [bloc, company],
  );

  const openAddEstimate = useCallback(
    () =>
      openSelectEstimateModal({
        orgId,
        onDone: () => bloc.paginated.reload().subscribe(),
      }),
    [bloc, orgId],
  );

  const buttons = useMemo(() => {
    return [
      {
        title: 'Add Estimate',
        onClick: openAddEstimate,
      },
    ];
  }, [openAddEstimate]);

  const content = useMemo(() => {
    return (
      <CBody>
        {loading.loading && <LoadingIndicator />}
        {!loading.loading && (
          <EmptySection
            items={estimates}
            title="Estimates"
            description="Create estimates to send to your clients. You can select an organization and choose specific estimate templates and forms to create them."
          >
            <GCTable
              noTableHead
              customStyles={{
                rows: {
                  style: { transition: 'all 0.25s ease-in-out' },
                  highlightOnHoverStyle: { backgroundColor: '#07456B20', cursor: 'pointer', borderRadius: 5 },
                },
              }}
              onRowClicked={row => {
                history.push(`/estimates/${row.id}`);
                // openEstimateDetail({ id: row.id, title: row.title })
              }}
              highlightOnHover
              pointerOnHover
              noSearch
              striped={false}
              bordered={false}
              noHeader
              columns={columns}
              data={estimates}
              paginated={bloc.paginated}
              paginationRowsPerPageOptions={[25, 50, 100]}
              noQueryOnSearch
            />
          </EmptySection>
        )}
      </CBody>
    );
  }, [estimates, loading]);

  if (viewType === 'tabular') {
    return (
      <>
        <div className="d-flex justify-content-end mb-2">
          <Button intent="primary" onClick={openAddEstimate}>
            Add Estimate
          </Button>
        </div>
        {content}
      </>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>Estimates</PageHeader>
        {content}
      </Container>
    </div>
  );
});
